import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, Package, ShieldCheck, Users } from "lucide-react";
import { useTranslation } from "react-i18next";

import { KpiCard } from "@/components/dashboard/KpiCard";
import { PendingApprovalsPanel } from "@/components/dashboard/PendingApprovalsPanel";
import { QuickActionsPanel } from "@/components/dashboard/QuickActionsPanel";
import { RecentActivityPanel } from "@/components/dashboard/RecentActivityPanel";
import {
  usePackagesSummary,
  usePendingApprovals,
  useUsersSummary,
} from "@/lib/api-hooks";

export const Route = createFileRoute("/_app/")({
  component: HomePage,
});

/**
 * Placeholder sparkline fino a quando avremo un endpoint /stats temporale
 * (Phase 8I polish / backend Phase 9 observability). Values normali
 * crescenti per dare un trend visivo positivo.
 */
const PLACEHOLDER_SPARK = [4, 6, 5, 7, 6, 9, 11];

export function HomePage(): JSX.Element {
  const { t } = useTranslation();
  const users = useUsersSummary();
  const packages = usePackagesSummary();
  const pending = usePendingApprovals();

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">
          {t("home.title")}
        </h1>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label={t("dashboard.kpi.users")}
          value={users.data?.total}
          loading={users.isLoading}
          error={users.error?.message}
          icon={Users}
          sparkline={{
            values: PLACEHOLDER_SPARK,
            ariaLabel: t("dashboard.kpi.users_spark_aria"),
          }}
        />
        <KpiCard
          label={t("dashboard.kpi.packages")}
          value={packages.data?.total}
          hint={
            packages.data
              ? `${packages.data.active} act · ${packages.data.suspended} susp`
              : undefined
          }
          loading={packages.isLoading}
          error={packages.error?.message}
          icon={Package}
        />
        <KpiCard
          label={t("dashboard.kpi.pending")}
          value={pending.data?.total ?? 0}
          hint={t("dashboard.kpi.pending_hint")}
          loading={pending.isLoading}
          error={pending.error?.message}
          tone={pending.data && pending.data.total > 0 ? "warn" : "default"}
          icon={AlertCircle}
        />
        <KpiCard
          label={t("dashboard.kpi.uptime")}
          value="99.8%"
          hint={t("dashboard.kpi.uptime_hint")}
          icon={ShieldCheck}
          tone="ok"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <PendingApprovalsPanel />
        </div>
        <QuickActionsPanel />
      </div>

      <RecentActivityPanel />
    </div>
  );
}
